import axios, { AxiosError } from '@cms-max/sls-api-sdk/node_modules/axios/index'
import { getAppBaseUrl } from 'utils/getAppBaseUrl'
import { SSOSignInOutApiFactory, AccountsApiFactory } from '@cms-max/sls-api-sdk'
import { trimTrailingSlash } from 'utils/urls/trim-trailing-slash'
import { Logger } from 'utils/logger'
import { slsApiBase } from 'test-utils/mock-api'
import { cookieInterfaces } from 'utils/cookies'

interface SlsErrorObject {
  field?: string
  name?: string
  message?: string
}

const appBasePath = process.env.REACT_APP_BASEPATH ?? ''
const slsUrl = process.env.REACT_APP_SLS_URL ?? getAppBaseUrl()

// SLS API configs
const slsAxiosInstance = axios.create({ withCredentials: true })
export const signInOutApi = SSOSignInOutApiFactory(undefined, trimTrailingSlash(slsUrl), slsAxiosInstance)

// Account API Config
const accountAxiosInstance = axios.create({ withCredentials: true })
// If you want to add an interceptor -> accountAxiosInstance.interceptors.request.use(() => console.log('hello world'))
const accountsApi = AccountsApiFactory(undefined, trimTrailingSlash(slsUrl), accountAxiosInstance)

type UpdateAccountCookieResponse = {
  updateAccountToken: string
}

// Since we're using relay param, this can be just base path
const getRedirectUri = () => {
  const url = new URL(window.location.href)
  return `${url.origin}${appBasePath}`
}

// This will get appended to the redirect URI
const getRelayParam = (redirectPath?: string) => {
  if (redirectPath) {
    return `${redirectPath}`
  }
  const url = new URL(window.location.href)
  return url.pathname.replace(appBasePath, '')
}

export const slsClient = {
  async getReqToken (lang: string) {
    try {
      // TODO: Once library is updated, pass params object instead of array of params
      const params = {
        clientId: 'max',
        lang,
        noRedirect: 'true'
      }
      const response = await signInOutApi.ssoAuthorizeGet(
        params.clientId, params.lang, undefined, undefined, undefined, params.noRedirect
      )
      // TODO: Remove type casting once library is updated
      return response.data as unknown as { 'req_token': string }
    } catch (error: unknown) {
      Logger.error(error)
      let errorMessage = 'Error getting data from SLSX'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      throw new Error(errorMessage)
    }
  },
  async logout () {
    try {
      await signInOutApi.ssoSignoutPost({})
    } catch (error: unknown) {
      Logger.error(error)
    }
  },
  async verifyPassword (currentUsername: string, currentPassword: string) {
    try {
      await accountsApi.accountApiUpdateAccountPost({
        username: currentUsername,
        password: currentPassword,
        updates: { password: currentPassword }
      })
      // GOOD SUCCESS - this means the users details were updated
      // TODO - add a logging db request here
      Logger.error('Received success while verifying password. This means somehow the password was updated')
      return true
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response && e.response.data) {
        const { data } = e.response
        if (Array.isArray(data.errors)) {
          const { errors } = data as { errors: SlsErrorObject[] }

          const errorMatchesLockoutResponse = ({ message }: SlsErrorObject) => message === 'user is locked'
          // This error indicates that the password was valid
          const errorMatchesValidPasswordResponse = ({ field, name }: SlsErrorObject) => field === 'password' && name === 'AlreadyUsed'
          const errorMatchesIncorrectPasswordResponse = ({ message, name }: SlsErrorObject) => name === 'Unauthorized' && message === 'incorrect password'

          if (e.response.status === 403 && errors.some(errorMatchesLockoutResponse)) {
            throw new Error('user is locked')
          }
          if (e.response.status === 401 && errors.some(errorMatchesIncorrectPasswordResponse)) {
            throw new Error('incorrect password')
          }
          if (e.response.status === 400 && errors.some(errorMatchesValidPasswordResponse)) {
            return true
          }
        }
      }
    }
    return false
  },
  async updateAccountCookie (password: string) {
    try {
      const response = await slsAxiosInstance.post<UpdateAccountCookieResponse>(
        `${slsApiBase}/account/api/update-account-cookie/`,
        {
          password
        }
      )
      return response.data.updateAccountToken
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response) {
        if (e.response.status === 403) {
          throw new Error('user is locked')
        }
        if (e.response.status === 401) {
          throw new Error('incorrect password')
        }
        if (e.response.status === 400) {
          throw new Error('missing password')
        }
      }
      throw new Error('unknown error')
    }
  },
  async updateAccount (options: Parameters<typeof accountsApi.accountApiUpdateAccountPost>[0]['updates']) {
    await accountsApi.accountApiUpdateAccountPost(
      // @ts-ignore -- we do not need username/password if we have the update-account-cookie
      {
        updates: options
      },
      {
        withCredentials: true,
        headers: {
          // Pass the csrf header - required per sls docs
          'x-sls-csrf': cookieInterfaces.slsCsrf.get()
        }
      })
  },
  async updateAccountDetails (options: Parameters<typeof accountsApi.accountApiUpdateAccountPost>[0]) {
    await accountsApi.accountApiUpdateAccountPost(options)
    // TODO - add this functionality for updating username and updating passwords
  },
  buildLoginUrl (language: string, redirectPath?: string): string {
    const url = new URL('sso/authorize', slsUrl)
    const paramTuples: [string, string][] = [
      ['client_id', 'max'],
      ['redirect_uri', getRedirectUri()],
      ['relay', getRelayParam(redirectPath)],
      ['lang', language === 'en' ? 'en-us' : 'es-mx']
    ]
    paramTuples.forEach(paramTuple => {
      url.searchParams.append(paramTuple[0], paramTuple[1])
    })

    return url.toString()
  }
}
